// MobileSheet — the shared mobile bottom-sheet shell.
//
// Any layout can opt into a mobile variant by rendering this at narrow widths
// (see useIsMobile) instead of its desktop side panel + status card. The sheet
// owns:
//
//   1. A draggable handle that expands / minimises the sheet. Expanded it is
//      capped to a fraction of the screen (CSS clamp); minimised it collapses
//      to a peek so the viewport is mostly clear.
//   2. A tab bar + the active tab's <Slot> content (e.g. Materials / Products).
//      A tab may instead be an "action tab" (e.g. Cart) that fires a callback
//      rather than swapping content — used to open the cart modal, optionally
//      with a count badge. When `selection` is set (a placed product is
//      selected) the tabs are replaced by the selection slot's content.
//
// Content is composed from the existing block/slot system: each content tab
// names a slotId and the sheet renders that slot's mapping. No new rendering
// primitives — the sheet is structural chrome around <Slot>.

import { useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from "react";
import { Slot } from "../blocks/Slot";
import type { BlockHostContext, SlotMapping } from "../blocks/types";

// Persisted open-height (px) of the sheet. The user can drag the top handle to
// resize; the chosen height is remembered so the sheet reopens to it.
const HEIGHT_STORAGE_KEY = "sd-mobile-sheet-height";
const PEEK_HEIGHT = 64;            // collapsed: handle + tab row only
const MIN_OPEN_HEIGHT = 160;       // can't shrink the open sheet below this
// Treat a handle press that moves less than this as a tap (toggle), not a drag.
const DRAG_TAP_THRESHOLD = 6;

function maxOpenHeight(): number {
  if (typeof window === "undefined") return 460;
  return Math.round(window.innerHeight * 0.85);
}
function clampHeight(h: number): number {
  return Math.max(MIN_OPEN_HEIGHT, Math.min(maxOpenHeight(), h));
}
function loadStoredHeight(): number {
  if (typeof window === "undefined") return 0;
  try {
    const raw = window.localStorage.getItem(HEIGHT_STORAGE_KEY);
    const n = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) ? clampHeight(n) : 0;
  } catch {
    return 0;
  }
}

export interface MobileSheetTab {
  /** Stable id — used as the React key and the active-tab discriminator. */
  id: string;
  /** Visible tab label (e.g. "Materials", "Products", "Cart"). */
  label: string;
  /** Slot id whose mapping renders as this tab's body. Omit for an action tab. */
  slotId?: string;
  /** Action tab: tapping fires this instead of switching content (e.g. open
   *  the cart modal). When set, `slotId` is ignored. */
  onSelect?: () => void;
  /** Optional numeric badge shown on the tab (e.g. cart item count). Hidden
   *  when null/0. */
  badge?: number | null;
}

export interface MobileSheetProps {
  tabs: MobileSheetTab[];
  /** Resolved slot mapping (layout default merged with any Designer override). */
  mapping: SlotMapping;
  host: BlockHostContext;
  /** Start expanded? Defaults to true so the panel is visible on first load. */
  defaultOpen?: boolean;
  /**
   * When a placed product is selected, the sheet shows this slot's content in
   * place of the tabs (e.g. the per-surface material picker). `title` heads the
   * panel; `onClose` deselects (the head's × button). Absent = normal tabbed view.
   */
  selection?: { slotId: string; title?: string | null; onClose?: () => void } | null;
}

export function MobileSheet({
  tabs,
  mapping,
  host,
  defaultOpen = true,
  selection = null,
}: MobileSheetProps) {
  const [open, setOpen] = useState(defaultOpen);
  // Open height (px). 0 means "not set yet" → CSS default applies. Seeded from
  // localStorage so a previously-dragged size is the reopen default.
  const [height, setHeight] = useState<number>(() => loadStoredHeight());
  // First content tab is the default active tab. Action tabs (no slotId) are
  // never "active" — they fire and return.
  const firstContentTab = tabs.find(t => t.slotId);
  const [activeTabId, setActiveTabId] = useState<string>(firstContentTab?.id ?? "");

  // If the tab set changes and the active tab no longer exists (or isn't a
  // content tab), fall back to the first content tab.
  useEffect(() => {
    const stillValid = tabs.some(t => t.id === activeTabId && t.slotId);
    if (!stillValid) {
      const next = tabs.find(t => t.slotId);
      if (next) setActiveTabId(next.id);
    }
  }, [tabs, activeTabId]);

  // Auto-fold on drag-start. On mobile only the product-row drag handle carries
  // [data-product-id]; a pointerdown there means a drag into the scene is
  // beginning, so collapse the sheet to get it out of the way. Capture phase so
  // we see it before the drag JS calls preventDefault/stopPropagation.
  useEffect(() => {
    const onPointerDown = (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest?.(".sd-block-catalog__row-drag")) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("touchstart", onPointerDown, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("touchstart", onPointerDown, true);
    };
  }, []);

  // When a selection appears, pop the sheet open so the picker is visible.
  const selectionActive = selection != null;
  useEffect(() => {
    if (selectionActive) setOpen(true);
  }, [selectionActive]);

  const activeTab = tabs.find(t => t.id === activeTabId) ?? firstContentTab;

  const onTabClick = (t: MobileSheetTab) => {
    // Action tab — fire and stop (don't switch content or change open state).
    if (t.onSelect) {
      t.onSelect();
      return;
    }
    setActiveTabId(t.id);
    if (!open) setOpen(true);
  };

  // ── Handle: drag to resize, tap to toggle ──────────────────────────────
  // pointerdown on the handle starts a resize gesture. Dragging up grows the
  // sheet, down shrinks it; the new height persists. If the pointer barely
  // moved by pointerup, it's a tap → toggle open/minimised instead.
  const [resizing, setResizing] = useState(false);
  const dragRef = useRef<{ startY: number; startHeight: number; moved: boolean } | null>(null);
  const onHandlePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    // Only the primary button / touch.
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const startHeight = height > 0 ? height : (open ? clampHeight(maxOpenHeight() * 0.6) : PEEK_HEIGHT);
    dragRef.current = { startY: e.clientY, startHeight, moved: false };
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const onHandlePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d) return;
    const dy = d.startY - e.clientY; // up = positive = taller
    if (!d.moved && Math.abs(dy) < DRAG_TAP_THRESHOLD) return; // still a tap
    d.moved = true;
    if (!resizing) setResizing(true);
    if (!open) setOpen(true); // dragging always opens
    setHeight(clampHeight(d.startHeight + dy));
  };
  const onHandlePointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    dragRef.current = null;
    setResizing(false);
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
    if (!d) return;
    if (!d.moved) {
      // Tap — toggle.
      setOpen(o => !o);
      return;
    }
    // Persist the dragged height as the new default.
    try {
      window.localStorage.setItem(HEIGHT_STORAGE_KEY, String(clampHeight(d.startHeight + (d.startY - e.clientY))));
    } catch { /* storage unavailable — keep in-memory only */ }
  };

  // Inline height: only when open, tabbed (not selection), and the user has set
  // one; otherwise let CSS own the heights. Selection mode is content-sized;
  // minimised uses the CSS peek height.
  const sheetStyle: CSSProperties | undefined =
    open && !selectionActive && height > 0 ? { maxHeight: height, height } : undefined;

  return (
    <div className="sd-mobile">
      <section
        className={
          "sd-mobile__sheet" +
          (open ? " sd-mobile__sheet--open" : "") +
          (resizing ? " sd-mobile__sheet--resizing" : "")
        }
        style={sheetStyle}
        aria-label="Configurator panel"
      >
        {/* Drag to resize, tap to toggle. Hidden in selection mode — that view
            is content-sized and dismissed via the head's × (deselect), not by
            dragging. A div (not a button) so pointer capture + drag math behave;
            keyboard users get the toggle button role + Enter/Space below. */}
        {!selectionActive && (
          <div
            className="sd-mobile__handle"
            role="button"
            tabIndex={0}
            aria-expanded={open}
            aria-label={open ? "Resize or minimise panel" : "Resize or expand panel"}
            onPointerDown={onHandlePointerDown}
            onPointerMove={onHandlePointerMove}
            onPointerUp={onHandlePointerUp}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setOpen(o => !o);
              }
            }}
          >
            <span className="sd-mobile__handle-bar" aria-hidden="true" />
          </div>
        )}

        {/* Tab bar + tab bodies stay mounted at all times — even while a
            selection is shown — so each tab's scroll position survives both tab
            switches and the selection coming and going. The selection overlay
            (below) is shown on top when active. The tab bar is hidden during
            selection (the selection head replaces it). */}
        {tabs.length > 1 && (
          <div className="sd-mobile__tabs" role="tablist" hidden={selectionActive}>
            {tabs.map(t => {
              const isContent = !!t.slotId;
              const isActive = isContent && t.id === activeTab?.id;
              const showBadge = typeof t.badge === "number" && t.badge > 0;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={
                    "sd-mobile__tab" +
                    (isActive ? " sd-mobile__tab--active" : "") +
                    (t.onSelect ? " sd-mobile__tab--action" : "")
                  }
                  onClick={() => onTabClick(t)}
                >
                  {t.label}
                  {showBadge && (
                    <span className="sd-mobile__tab-badge">{t.badge}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Each content tab is mounted in its own scroll container; only the
            active one shows (and all hide while a selection is active). Keeping
            them mounted preserves each tab's scroll position across tab switches
            and selection enter/exit — an unmount would reset scrollTop to 0. */}
        {tabs
          .filter(t => t.slotId)
          .map(t => {
            const isActive = !selectionActive && t.id === activeTab?.id;
            return (
              <div
                key={t.id}
                className="sd-mobile__body"
                role="tabpanel"
                hidden={!isActive}
              >
                <Slot
                  slotId={t.slotId!}
                  mapping={mapping[t.slotId!]}
                  host={host}
                  className="sd-mobile__tab-content"
                />
              </div>
            );
          })}

        {/* Selection overlay — shown on top when a placed product is selected.
            Head row: product name (left) + action buttons (right). Body: the
            per-surface material picker. The slot's own name/hairline are
            filtered out so nothing duplicates. */}
        {selectionActive && (() => {
          const entries = mapping[selection!.slotId] ?? [];
          const headActions = entries.filter(b => b.block === "action");
          const bodyEntries = entries.filter(
            b => b.block !== "action" && b.block !== "text" && b.block !== "line"
          );
          return (
            <>
              <div className="sd-mobile__selection-head">
                {selection?.title && (
                  <span className="sd-mobile__selection-title">{selection.title}</span>
                )}
                <div className="sd-mobile__selection-actions">
                  {headActions.length > 0 && (
                    <Slot
                      slotId={selection!.slotId + ":actions"}
                      mapping={headActions}
                      host={host}
                      inline
                    />
                  )}
                  {selection?.onClose && (
                    // Corner close = deselect. No grab handle on the selection
                    // view (unlike the tabbed sheet); the × dismisses it.
                    <button
                      type="button"
                      className="sd-mobile__selection-close"
                      onClick={selection.onClose}
                      aria-label="Close selection"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
              <div className="sd-mobile__body sd-mobile__body--selection" role="tabpanel">
                <Slot
                  slotId={selection!.slotId}
                  mapping={bodyEntries}
                  host={host}
                  className="sd-mobile__tab-content"
                />
              </div>
            </>
          );
        })()}
      </section>
    </div>
  );
}
